import "@ui5/webcomponents-icons/dist/accounting-document-verification.js";
import "@ui5/webcomponents-icons/dist/action-settings.js";
import "@ui5/webcomponents-icons/dist/approvals.js";
import "@ui5/webcomponents-icons/dist/download.js";
import "@ui5/webcomponents-icons/dist/email.js";
import "@ui5/webcomponents-icons/dist/history.js";
import "@ui5/webcomponents-icons/dist/inbox.js";
import "@ui5/webcomponents-icons/dist/machine.js";
import "@ui5/webcomponents-icons/dist/number-sign.js";
import "@ui5/webcomponents-icons/dist/settings.js";
import "@ui5/webcomponents-icons/dist/group.js";
import "@ui5/webcomponents-icons/dist/home.js";
import "@ui5/webcomponents-icons/dist/it-host.js";
import "@ui5/webcomponents-icons/dist/key.js";
import "@ui5/webcomponents-icons/dist/log.js";
import "@ui5/webcomponents-icons/dist/role.js";
import "@ui5/webcomponents-icons/dist/sys-monitor.js";
import "@ui5/webcomponents-icons/dist/user-settings.js";

import { useApiClient } from "@positive/api-client";
import { AppShell, idleTimeoutDefaults, useShortcut, type UserMenuItem } from "@positive/ui";
import { Toast } from "@ui5/webcomponents-react";
import { Outlet, useMatches, useNavigate, useRouteContext, useRouterState } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ForbiddenPage } from "./ForbiddenPage";
import { useContextHelp } from "./help/useContextHelp";
import { accessDenial, filterNavigation, isAccessAllowed } from "./identity/access";
import { BranchSwitchDialog } from "./identity/BranchSwitchDialog";
import { useIdentity } from "./identity/IdentityProvider";
import { ImpersonationBanner } from "./identity/ImpersonationBanner";
import { localName } from "./identity/session";
import { useNotificationCenter } from "./notifications/useNotificationCenter";
import { usePreferences } from "./PreferencesContext";

const pinVerifyPath = "platform/me/pos-pin/verify";

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/u).filter(Boolean);
  return (
    parts.length > 1 ? `${parts[0]?.[0] ?? ""}${parts.at(-1)?.[0] ?? ""}` : (parts[0]?.slice(0, 2) ?? "")
  ).toUpperCase();
}

/**
 * Shell around every route (ADR-0010/D-2): navigation filtered by permissions, route guard (deny by default),
 * company/branch switcher (P12), user menu (profile, change password via Keycloak, sign out), idle lock per the
 * session's idle policy (P4) and the support-session banner (A3).
 */
export function RootLayout() {
  const { t, i18n } = useTranslation("shell");
  const navigate = useNavigate();
  const client = useApiClient();
  const { navigation } = useRouteContext({ from: "__root__" });
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const matches = useMatches();
  const { language, theme, setLanguage, setTheme } = usePreferences();
  // D16: context help - the Shell Bar button and F1 open this screen's page of the user manual.
  const help = useContextHelp();
  useShortcut("manual", help.open);
  const { access, session, auth, displayName, mode } = useIdentity();
  const [contextOpen, setContextOpen] = useState(false);
  const notificationCenter = useNotificationCenter();
  // Result of Keycloak's change-password page (application-initiated action), shown once after the redirect.
  const [kcAction, setKcAction] = useState(() => auth.takeKcActionStatus());

  const visibleNavigation = useMemo(() => filterNavigation(navigation, access), [access, navigation]);
  const leaf = matches.at(-1);
  const isRootOnly = leaf?.routeId === "__root__";
  const allowed = isRootOnly || isAccessAllowed(leaf?.staticData.access, access);
  const denial = allowed ? null : accessDenial(leaf?.staticData.access, access);
  const posLock = leaf?.staticData.lockMode === "pos";

  const idleTimeoutMs = session
    ? (posLock ? session.idlePolicy.posLockMinutes : session.idlePolicy.backOfficeIdleMinutes) * 60_000
    : posLock
      ? idleTimeoutDefaults.pos
      : idleTimeoutDefaults.backOffice;

  const company = session?.companies.find((item) => item.id === session.session.companyId);
  const branch = session?.branches.find((item) => item.id === session.session.branchId);
  const contextLabel =
    company && branch
      ? session?.session.allCompanies
        ? t("context.labelAllCompanies", { branch: localName(branch, i18n.language) })
        : t("context.label", { company: localName(company, i18n.language), branch: localName(branch, i18n.language) })
      : null;

  const userMenuItems: UserMenuItem[] = [
    ...(mode.kind === "tenant" ? [{ key: "profile", text: t("user.profile"), icon: "user-settings" }] : []),
    { key: "password", text: t("user.changePassword"), icon: "key" },
    { key: "signOut", text: t("user.signOut"), icon: "log" },
  ];

  return (
    <AppShell
      navigation={visibleNavigation}
      currentPath={pathname}
      language={language}
      theme={theme}
      onNavigate={(path) => void navigate({ to: path })}
      onLanguageChange={setLanguage}
      onThemeChange={setTheme}
      notifications={notificationCenter.notifications}
      notificationsUnread={notificationCenter.unread}
      onNotificationsMarkAllRead={notificationCenter.markAllRead}
      onNotificationOpen={(notification) => {
        const link = notificationCenter.open(notification);
        if (link) void navigate({ to: link });
      }}
      idleTimeoutMs={idleTimeoutMs}
      idleLock={{
        mode: posLock ? "pos" : "backOffice",
        reauthenticate: () => auth.reauthenticate(),
        onSignIn: () => void auth.signIn(),
        verifyPin: async (pin) => {
          await client.post(pinVerifyPath, { pin });
        },
        onSignOut: () => void auth.signOut(),
      }}
      user={{ name: displayName, initials: initialsOf(displayName) }}
      userMenuItems={userMenuItems}
      onUserMenuItem={(key) => {
        if (key === "profile") void navigate({ to: "/me/profile" });
        else if (key === "password") void auth.changePassword();
        else if (key === "signOut") void auth.signOut();
      }}
      {...(contextLabel
        ? {
            context: {
              label: contextLabel,
              onClick: () => {
                setContextOpen(true);
              },
            },
          }
        : {})}
      onHelp={help.open}
      banner={session?.impersonation ? <ImpersonationBanner impersonation={session.impersonation} /> : undefined}
    >
      {allowed ? <Outlet /> : <ForbiddenPage {...(denial?.reason === "module" ? { module: denial.module } : {})} />}
      <Toast
        open={kcAction !== null}
        data-testid="kc-action-toast"
        onClose={() => {
          setKcAction(null);
        }}
      >
        {kcAction === "success" ? t("auth.passwordChanged") : t("auth.passwordUnchanged")}
      </Toast>
      {session && contextOpen && (
        <BranchSwitchDialog
          open
          session={session}
          onClose={() => {
            setContextOpen(false);
          }}
        />
      )}
    </AppShell>
  );
}
